import {
  COMPRESSIBLE_IMAGE_TYPES,
  COMPRESSIBLE_VIDEO_TYPES,
  MIN_VIDEO_BITRATE_BPS,
  PNG_HEADER_BYTES,
  TRANSCODE_TO_JPEG_IMAGE_TYPES,
  VIDEO_DIMENSION_TIERS,
  VIDEO_SIZE_SAFETY_FACTOR,
  WAPI_ACCEPTED_IMAGE_TYPES,
  WAPI_ACCEPTED_VIDEO_TYPES,
} from './MediaCompression.constants';
import {
  tTargetWidthOptions,
  tVideoBitrateBudgetOptions,
} from './MediaCompression.types';

export function getCompressibleKind(file: File): 'image' | 'video' | null {
  const type = file.type.toLowerCase();
  if (COMPRESSIBLE_IMAGE_TYPES.includes(type)) return 'image';
  if (COMPRESSIBLE_VIDEO_TYPES.includes(type)) return 'video';
  return null;
}

// Shared HEIC/HEIF detection atoms — single source of truth for both the
// send-path detector (isTranscodeOnlyImage, over a File) and the render-path
// detector (ImageContent's isHeicAttachment, over a persisted attachment). The
// `(\?|$)` tolerates a CDN URL query suffix and still matches bare filenames.
const HEIC_EXTENSION_RE = /\.(heic|heif)(\?|$)/i;

export function isHeicMimeType(mimeType: string): boolean {
  return TRANSCODE_TO_JPEG_IMAGE_TYPES.includes(mimeType.toLowerCase());
}

// HEIC/HEIF files frequently arrive with an empty or generic File.type (the
// browser doesn't map the extension to a MIME), so callers fall back to this.
export function hasHeicExtension(nameOrUrl: string): boolean {
  return HEIC_EXTENSION_RE.test(nameOrUrl);
}

// The minimum shape the detector needs. A `File` satisfies it structurally, and
// so does a `tChatAttachment` — the attachment tray holds metadata, not Files,
// but its gates must reach the same verdict as the send path's.
export type tTranscodeCandidate = {
  type: string;
  name: string;
};

/**
 * True when the image must be transcoded to JPEG for browser compatibility
 * (HEIC/HEIF). Detects by MIME first, then by filename extension only when the
 * browser reported no browser-renderable image type. A real JPEG/PNG mislabeled
 * `.heic` (`type: image/jpeg`) must NOT be force-decoded — libheif would fail on
 * its bytes and the file would be wrongly blocked from sending. Such a file is
 * already renderable, so it needs no transcode.
 */
export function isTranscodeOnlyMedia(candidate: tTranscodeCandidate): boolean {
  const type = candidate.type.toLowerCase();
  if (isHeicMimeType(type)) return true;
  if (type.startsWith('image/')) return false;
  return hasHeicExtension(candidate.name);
}

/** `isTranscodeOnlyMedia` over a real File — the send-path entry point. */
export function isTranscodeOnlyImage(file: File): boolean {
  return isTranscodeOnlyMedia(file);
}

/**
 * Bits per second available to the video stream so video + audio + container
 * overhead fit inside maxBytes. Returns null when the achievable bitrate
 * would be unwatchable (the video is too long for the byte limit).
 */
export function computeVideoBitrateBudget(
  options: tVideoBitrateBudgetOptions,
): number | null {
  const { maxBytes, durationSeconds, audioBitrate } = options;
  if (durationSeconds <= 0) return null;

  const totalBits = maxBytes * 8 * VIDEO_SIZE_SAFETY_FACTOR;
  const videoBitrate = Math.floor(totalBits / durationSeconds - audioBitrate);

  if (videoBitrate < MIN_VIDEO_BITRATE_BPS) return null;
  return videoBitrate;
}

export function pickLongestEdgeCap(videoBitrate: number): number {
  for (const [minBitrate, cap] of VIDEO_DIMENSION_TIERS) {
    if (videoBitrate >= minBitrate) return cap;
  }
  return VIDEO_DIMENSION_TIERS[VIDEO_DIMENSION_TIERS.length - 1][1];
}

/**
 * Width (forced even — H.264 4:2:0 requires it) the video must be scaled to
 * so its longest edge fits longestEdgeCap. Returns null when the source
 * already fits: never upscale.
 */
export function computeTargetWidth(
  options: tTargetWidthOptions,
): number | null {
  const { width, height, longestEdgeCap } = options;
  const longestEdge = Math.max(width, height);
  if (longestEdge <= longestEdgeCap) return null;

  const scale = longestEdgeCap / longestEdge;
  const targetWidth = Math.floor((width * scale) / 2) * 2;
  return Math.max(2, targetWidth);
}

export function replaceFileExtension(
  fileName: string,
  newExtension: string,
): string {
  const dotIndex = fileName.lastIndexOf('.');
  const base = dotIndex > 0 ? fileName.slice(0, dotIndex) : fileName;
  return `${base}${newExtension}`;
}

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
const IHDR_CHUNK_TYPE = [0x49, 0x48, 0x44, 0x52];
const IHDR_TYPE_OFFSET = 12;
const IHDR_BIT_DEPTH_OFFSET = 24;

function bytesMatchAt(
  bytes: Uint8Array,
  options: { offset: number; expected: number[] },
): boolean {
  const { offset, expected } = options;
  return expected.every((byte, index) => bytes[offset + index] === byte);
}

/**
 * Bits per channel declared in a PNG's IHDR chunk, which the spec pins as the
 * first chunk right after the signature. Returns null when the bytes are not
 * a PNG header.
 */
export function readPngBitDepth(bytes: Uint8Array): number | null {
  if (bytes.length < PNG_HEADER_BYTES) return null;
  if (!bytesMatchAt(bytes, { offset: 0, expected: PNG_SIGNATURE })) return null;
  if (
    !bytesMatchAt(bytes, { offset: IHDR_TYPE_OFFSET, expected: IHDR_CHUNK_TYPE })
  ) {
    return null;
  }
  return bytes[IHDR_BIT_DEPTH_OFFSET];
}

const RIFF_SIGNATURE = [0x52, 0x49, 0x46, 0x46];
const WEBP_FORM_TYPE = [0x57, 0x45, 0x42, 0x50];
const WEBP_FORM_TYPE_OFFSET = 8;
const VP8X_CHUNK_TYPE = [0x56, 0x50, 0x38, 0x58];
const WEBP_FIRST_CHUNK_OFFSET = 12;
const VP8X_FLAGS_OFFSET = 20;
const VP8X_ANIMATION_FLAG = 0x02;

/**
 * INT-1683: whether the bytes are a WebP and, if so, whether it is animated,
 * judged by the bytes and not by the type the browser reports. A WebP is
 * animated only when its first chunk is VP8X with the animation flag; a
 * simple VP8 or VP8L file is a still. Returns null when the bytes are not a
 * WebP header, or too short to tell.
 */
export function readWebpKind(bytes: Uint8Array): 'static' | 'animated' | null {
  if (bytes.length < WEBP_FIRST_CHUNK_OFFSET + VP8X_CHUNK_TYPE.length) return null;
  if (!bytesMatchAt(bytes, { offset: 0, expected: RIFF_SIGNATURE })) return null;
  if (
    !bytesMatchAt(bytes, { offset: WEBP_FORM_TYPE_OFFSET, expected: WEBP_FORM_TYPE })
  ) {
    return null;
  }
  if (
    !bytesMatchAt(bytes, { offset: WEBP_FIRST_CHUNK_OFFSET, expected: VP8X_CHUNK_TYPE })
  ) {
    return 'static';
  }
  if (bytes.length <= VP8X_FLAGS_OFFSET) return null;
  return bytes[VP8X_FLAGS_OFFSET] & VP8X_ANIMATION_FLAG ? 'animated' : 'static';
}

/** Expects a normalized mimetype (image/jpg already folded into image/jpeg). */
export function isWapiAcceptedType(type: string): boolean {
  const normalized = type.toLowerCase();
  return (
    WAPI_ACCEPTED_IMAGE_TYPES.includes(normalized) ||
    WAPI_ACCEPTED_VIDEO_TYPES.includes(normalized)
  );
}
